<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert, useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import MessageApi from 'dashboard/api/inbox/message.js';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  DV_INSERT_DRAFT_EVENT,
  assistantNoDraftReason,
  assistantNoteFromAttributes,
} from 'dashboard/helper/dvNotes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

const MODEL_OPTIONS = [
  {
    value: 'auto',
    label: 'Auto',
    hint: 'Сам выберет, где Sonnet, а где уже нужен Opus.',
  },
  {
    value: 'sonnet',
    label: 'Sonnet',
    hint: 'Быстрее. Хорошо для FAQ и обычных уточнений.',
  },
  {
    value: 'opus',
    label: 'Opus',
    hint: 'Тяжелая артиллерия для сложных и мутных кейсов.',
  },
];

// DV: the agent run budget on the backend is 200 s, keep polling a bit longer.
const ASSISTANT_POLL_DEADLINE_MS = 230000;
const DV_AGENT_ACCOUNT_ID = 1;
const DV_OPEN_WA_TEMPLATES_EVENT = 'dv:open-whatsapp-templates';

const EXECUTION_MODE_OPTIONS = [
  {
    value: 'draft',
    label: 'Черновик',
    hint: 'Агент только соберёт ответ и рекомендации. Без live-изменений в бронях.',
  },
  {
    value: 'operator_command',
    label: 'Операторская команда',
    hint: 'Если фактов хватает, агент может сразу создать, изменить, отменить, восстановить бронь или перевести её в нужный статус без второго approve.',
  },
];

function prepareAssistantDraftForRichEditor(content) {
  return String(content || '')
    .trim()
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => {
      const trimmedRight = line.replace(/[ \t]+$/u, '');
      return trimmedRight ? `${trimmedRight}  ` : '';
    })
    .join('\n');
}

export default {
  name: 'ReplyTopPanel',
  components: {
    Dialog,
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const { accountId } = useAccount();
    const showCopilotMenu = ref(false);
    // Filled in mounted(): the options-API method is not reachable from setup.
    const quickDraftHandler = ref(null);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyB': {
        action: () => quickDraftHandler.value?.(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      accountId,
      quickDraftHandler,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  data() {
    return {
      agentPrompt: '',
      requestedModel: 'auto',
      assistantExecutionMode: 'draft',
      assistantDraft: '',
      assistantError: '',
      assistantStatus: '',
      activeAssistantRequestId: '',
      activeTriggerMessageId: '',
      isAssistantDialogOpen: false,
      isAssistantRunning: false,
      assistantElapsedSeconds: 0,
      assistantTimer: null,
      isAlive: true,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
    assistantModelOptions() {
      return MODEL_OPTIONS;
    },
    selectedModelHint() {
      return (
        this.assistantModelOptions.find(
          option => option.value === this.requestedModel
        )?.hint || ''
      );
    },
    assistantExecutionModeOptions() {
      return EXECUTION_MODE_OPTIONS;
    },
    selectedExecutionModeHint() {
      return (
        this.assistantExecutionModeOptions.find(
          option => option.value === this.assistantExecutionMode
        )?.hint || ''
      );
    },
    isOperatorCommandMode() {
      return this.assistantExecutionMode === 'operator_command';
    },
    assistantRunButtonLabel() {
      return this.isOperatorCommandMode
        ? 'Применить и собрать ответ'
        : 'Запустить агента';
    },
    isDvAccount() {
      return this.accountId === DV_AGENT_ACCOUNT_ID;
    },
    // DV: WhatsApp templates sit where Chatwoot's Copilot sparkle was.
    showDvWhatsappTemplates() {
      if (!this.isDvAccount || this.mode === REPLY_EDITOR_MODES.NOTE) return false;
      const inboxId = this.$store.getters.getSelectedChat?.inbox_id;
      if (!inboxId) return false;
      const templates =
        this.$store.getters['inboxes/getFilteredWhatsAppTemplates'](inboxId);
      return Array.isArray(templates) && templates.length > 0;
    },
    quickDraftLabel() {
      return this.isAssistantRunning
        ? `Пишу… ${this.assistantElapsedSeconds} с`
        : 'Черновик';
    },
    canRunAssistant() {
      return !this.disabled && !!this.conversationId && !this.isAssistantRunning;
    },
    canInsertAssistantDraft() {
      return !this.isAssistantRunning && !!this.assistantDraft;
    },
    selectedChatMessages() {
      const messages = this.$store.getters.getSelectedChat?.messages;
      return Array.isArray(messages) ? messages : [];
    },
    assistantButtonClass() {
      // Tailwind needs literal class names, so both variants are spelled out.
      if (this.isDvAccount) {
        return this.isAssistantDialogOpen
          ? 'text-n-blue-11 bg-n-blue-4'
          : 'text-n-blue-11 hover:enabled:!bg-n-blue-4';
      }
      return {
        'text-n-blue-11 hover:enabled:!bg-n-blue-3': !this.isAssistantDialogOpen,
        'text-n-blue-11 bg-n-blue-3': this.isAssistantDialogOpen,
      };
    },
  },
  watch: {
    conversationId() {
      this.resetAssistantState();
      this.requestedModel = 'auto';
      this.assistantExecutionMode = 'draft';
      this.agentPrompt = '';
      this.dismissAssistantDialog();
    },
  },
  mounted() {
    this.quickDraftHandler = () => {
      if (this.isDvAccount) this.quickDraft();
    };
    emitter.on(DV_INSERT_DRAFT_EVENT, this.insertDraftFromNote);
  },
  beforeUnmount() {
    emitter.off(DV_INSERT_DRAFT_EVENT, this.insertDraftFromNote);
    this.isAlive = false;
    this.activeAssistantRequestId = '';
    this.quickDraftHandler = null;
    this.stopAssistantTimer();
  },
  methods: {
    openDvWhatsappTemplates() {
      emitter.emit(DV_OPEN_WA_TEMPLATES_EVENT);
    },
    openAssistantDialog() {
      if (!this.isAssistantRunning) {
        this.assistantError = '';
        this.assistantStatus = this.assistantDraft
          ? 'Черновик уже готов. Можно вставлять или перезапустить агента.'
          : '';
      }
      this.isAssistantDialogOpen = true;
      this.$refs.assistantDialog?.open();
    },
    dismissAssistantDialog() {
      this.$refs.assistantDialog?.close();
    },
    handleAssistantDialogClose() {
      this.isAssistantDialogOpen = false;
    },
    selectRequestedModel(model) {
      this.requestedModel = model;
    },
    selectAssistantExecutionMode(mode) {
      this.assistantExecutionMode = mode;
    },
    async quickDraft() {
      if (!this.canRunAssistant) {
        return;
      }

      const conversationId = this.conversationId;
      this.requestedModel = 'auto';
      this.assistantExecutionMode = 'draft';
      this.agentPrompt = '';

      await this.runAssistant();

      if (
        !this.isAlive ||
        this.conversationId !== conversationId ||
        !this.assistantDraft
      ) {
        return;
      }

      const editorIsEmpty = !String(this.editorContent || '').trim();
      if (this.isReplyRestricted || !editorIsEmpty) {
        // Never overwrite what the manager already typed: show the draft instead.
        this.openAssistantDialog();
        return;
      }

      if (this.mode !== REPLY_EDITOR_MODES.REPLY) {
        this.$emit('setReplyMode', REPLY_EDITOR_MODES.REPLY);
        await this.$nextTick();
      }
      this.insertAssistantDraft();
      useAlert('Черновик вставлен в ответ. Проверьте перед отправкой.');
    },
    async insertDraftFromNote(draft) {
      if (!this.isDvAccount || !draft) {
        return;
      }

      if (this.isReplyRestricted) {
        try {
          await copyTextToClipboard(draft);
          useAlert('Окно ответа закрыто, поэтому текст скопирован в буфер.');
        } catch (error) {
          useAlert('Окно ответа закрыто, а скопировать текст не вышло.');
        }
        return;
      }

      if (this.mode !== REPLY_EDITOR_MODES.REPLY) {
        this.$emit('setReplyMode', REPLY_EDITOR_MODES.REPLY);
        await this.$nextTick();
      }

      const content = prepareAssistantDraftForRichEditor(draft);
      if (!content) {
        return;
      }
      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content);
      useAlert('Черновик вставлен в ответ. Проверьте перед отправкой.');
    },
    startAssistantTimer() {
      this.stopAssistantTimer();
      const startedAt = Date.now();
      this.assistantElapsedSeconds = 0;
      this.assistantTimer = window.setInterval(() => {
        this.assistantElapsedSeconds = Math.round(
          (Date.now() - startedAt) / 1000
        );
      }, 1000);
    },
    stopAssistantTimer() {
      if (this.assistantTimer) {
        window.clearInterval(this.assistantTimer);
        this.assistantTimer = null;
      }
    },
    resetAssistantState() {
      this.assistantDraft = '';
      this.assistantError = '';
      this.assistantStatus = '';
      this.activeAssistantRequestId = '';
      this.activeTriggerMessageId = '';
      this.isAssistantRunning = false;
      this.stopAssistantTimer();
    },
    buildAssistantRequestId() {
      if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
      }

      return `dv-agent-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    },
    async runAssistant() {
      if (!this.canRunAssistant) {
        return;
      }

      if (
        this.isOperatorCommandMode &&
        !window.confirm(
          'Операторская команда может сразу изменить бронь (создать, отменить, перенести). Запускаем?'
        )
      ) {
        return;
      }

      const requestId = this.buildAssistantRequestId();

      this.assistantDraft = '';
      this.assistantError = '';
      this.assistantStatus = 'Агент уже пошел в бой и думает над черновиком.';
      if (this.isOperatorCommandMode) {
        this.assistantStatus =
          'Агент уже пошел в бой: проверяет кейс, и если всё сходится, сразу применит live-действие по брони.';
      }
      this.activeAssistantRequestId = requestId;
      this.activeTriggerMessageId = '';
      this.isAssistantRunning = true;
      this.startAssistantTimer();

      try {
        const response = await MessageApi.create({
          conversationId: this.conversationId,
          message: '/bro',
          private: true,
          contentAttributes: {
            source_module: 'chatwoot_agent_launcher',
            assistant_request: {
              request_id: requestId,
              prompt: this.agentPrompt.trim(),
              requested_model: this.requestedModel,
              requested_from: 'reply_top_panel',
              execution_mode: this.assistantExecutionMode,
            },
          },
        });

        this.activeTriggerMessageId = String(response?.data?.id || '');

        const assistantNote = await this.pollAssistantNote(requestId);
        const draftText = this.extractDraftFromAssistantNote(assistantNote);

        if (!this.isAlive || this.activeAssistantRequestId !== requestId) {
          return;
        }

        if (!draftText) {
          // The agent deliberately wrote no draft (e.g. no new question):
          // explain why instead of inserting the service text into the reply.
          const note = assistantNoteFromAttributes(
            this.contentAttributesForMessage(assistantNote)
          );
          const reason = assistantNoDraftReason(
            String(assistantNote?.content || ''),
            note?.intent || ''
          );
          this.assistantStatus = `Агент не стал писать черновик: ${reason}`;
          useAlert(this.assistantStatus);
          await this.cleanupLauncherNote(this.activeTriggerMessageId);
          return;
        }

        this.assistantDraft = draftText;
        this.assistantStatus = 'Черновик готов. Можно вставлять в ответ.';

        await this.cleanupLauncherNote(this.activeTriggerMessageId);
      } catch (error) {
        if (!this.isAlive || this.activeAssistantRequestId !== requestId) {
          return;
        }

        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          'Агент не ответил вовремя. Похоже, драматургия у него сегодня сильнее дисциплины.';

        this.assistantError = errorMessage;
        this.assistantStatus = '';
        useAlert(errorMessage);
      } finally {
        if (this.activeAssistantRequestId === requestId) {
          this.isAssistantRunning = false;
          this.stopAssistantTimer();
        }
      }
    },
    async pollAssistantNote(requestId) {
      const deadline = Date.now() + ASSISTANT_POLL_DEADLINE_MS;

      while (Date.now() < deadline) {
        if (!this.isAlive || this.activeAssistantRequestId !== requestId) {
          return null;
        }

        const fromStore = this.findAssistantNote(
          this.selectedChatMessages,
          requestId,
          this.activeTriggerMessageId
        );
        if (fromStore) {
          return fromStore;
        }

        const { data } = await MessageApi.getPreviousMessages({
          conversationId: this.conversationId,
          after: this.activeTriggerMessageId || undefined,
        });
        const payload = Array.isArray(data?.payload) ? data.payload : [];
        const fromApi = this.findAssistantNote(
          payload,
          requestId,
          this.activeTriggerMessageId
        );

        if (fromApi) {
          return fromApi;
        }

        await this.sleep(1500);
      }

      throw new Error(
        'Не дождались ответа агента почти за 4 минуты. Он, видимо, ушел за истиной и не вернулся к дедлайну.'
      );
    },
    contentAttributesForMessage(message) {
      return (
        message?.content_attributes ||
        message?.contentAttributes ||
        {}
      );
    },
    findAssistantNote(messages, requestId, triggerMessageId = '') {
      return [...messages]
        .reverse()
        .find(message => {
          const contentAttributes = this.contentAttributesForMessage(message);
          const sourceModule = String(
            contentAttributes?.source_module || contentAttributes?.sourceModule || ''
          );
          const assistantRequestId = String(
            contentAttributes?.assistant_request_id ||
              contentAttributes?.assistantRequestId ||
              ''
          );
          const automationAction = String(
            contentAttributes?.automation_action ||
              contentAttributes?.automationAction ||
              ''
          );

          return (
            sourceModule === 'assistant_claude' &&
            (
              assistantRequestId === requestId ||
              (
                !assistantRequestId &&
                !!triggerMessageId &&
                automationAction === `assistant_claude:${triggerMessageId}`
              )
            )
          );
        });
    },
    extractDraftFromAssistantNote(message) {
      if (!message) {
        return '';
      }

      const contentAttributes = this.contentAttributesForMessage(message);
      const structuredDraft = String(
        contentAttributes?.assistant_draft_text ||
          contentAttributes?.assistantDraftText ||
          ''
      ).trim();

      // Only the structured field is a draft; the note body may be a
      // «no draft» explanation that must never go to the client.
      return structuredDraft;
    },
    async cleanupLauncherNote(messageId) {
      if (!messageId) {
        return;
      }

      try {
        await MessageApi.delete(this.conversationId, messageId);
      } catch (error) {
        // If cleanup fails, we keep going. The technical note is ugly, not fatal.
      }
    },
    insertAssistantDraft() {
      if (!this.assistantDraft) {
        return;
      }

      const content = prepareAssistantDraftForRichEditor(this.assistantDraft);
      if (!content) {
        return;
      }

      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content);
      this.dismissAssistantDialog();
    },
    sleep(ms) {
      return new Promise(resolve => {
        window.setTimeout(resolve, ms);
      });
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-2 my-0 sm:mx-4">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div
      v-if="isDvAccount || captainTasksEnabled"
      class="flex items-center gap-2"
    >
      <div
        v-if="isDvAccount"
        class="flex items-center rounded-lg bg-n-blue-3 text-n-blue-11"
      >
        <button
          type="button"
          class="inline-flex h-8 items-center gap-1.5 rounded-l-lg px-2.5 text-sm font-medium text-n-blue-11 transition-colors hover:enabled:bg-n-blue-4 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="disabled || !conversationId || isAssistantRunning"
          title="Черновик ответа от DV Agent (Alt+B)"
          @click="quickDraft"
        >
          <span
            v-if="isAssistantRunning"
            class="i-lucide-loader-circle size-4 shrink-0 animate-spin"
          />
          <span v-else class="i-lucide-sparkles size-4 shrink-0" />
          <span class="hidden whitespace-nowrap sm:inline">
            {{ quickDraftLabel }}
          </span>
          <span
            v-if="isAssistantRunning"
            class="whitespace-nowrap tabular-nums sm:hidden"
          >
            {{ assistantElapsedSeconds }} с
          </span>
        </button>
        <NextButton
          ghost
          sm
          icon="i-lucide-chevron-down"
          :disabled="disabled || !conversationId"
          :class="assistantButtonClass"
          class="!rounded-l-none border-l border-n-blue-5"
          title="DV Agent: подсказка, модель, операторская команда"
          @click="openAssistantDialog"
        />
      </div>
      <NextButton
        v-else
        ghost
        sm
        icon="i-woot-captain"
        :disabled="disabled || !conversationId || isAssistantRunning"
        :class="assistantButtonClass"
        title="DV Agent"
        @click="openAssistantDialog"
      />
      <button
        v-if="showDvWhatsappTemplates"
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-lg bg-n-teal-3 px-2.5 text-sm font-medium text-n-teal-11 transition-colors hover:enabled:bg-n-teal-4 disabled:cursor-not-allowed disabled:opacity-70"
        :disabled="disabled"
        title="Шаблоны WhatsApp — можно писать и после 24 часов"
        data-dv-wa-templates
        @click="openDvWhatsappTemplates"
      >
        <span class="i-ph-whatsapp-logo size-4 shrink-0" />
        <span class="hidden whitespace-nowrap sm:inline">Шаблон</span>
      </button>
      <div v-if="captainTasksEnabled && !isDvAccount" class="relative">
        <NextButton
          ref="copilotToggleRef"
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
            'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
          }"
          sm
          icon="i-ph-sparkle-fill"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="[
            handleClickOutside,
            { ignore: [copilotToggleRef] },
          ]"
          :has-selection="false"
          :has-content="hasContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <NextButton
        ghost
        class="text-n-slate-11"
        :class="{ 'max-sm:!hidden': isDvAccount }"
        sm
        icon="i-lucide-maximize-2"
        @click="$emit('toggleEditorSize')"
      />
    </div>
  </div>

  <Dialog
    ref="assistantDialog"
    width="2xl"
    position="top"
    scroll-content
    title="DV Agent"
    description="Оставьте подсказку для ответа или переключите режим в настоящий боевой, если надо не только подумать, но и сразу применить live-действие по брони."
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleAssistantDialogClose"
  >
    <div
      class="flex min-h-0 flex-col gap-5"
    >
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Подсказка оператора
        </label>
        <textarea
          v-model="agentPrompt"
          rows="4"
          class="w-full px-4 py-3 text-sm border rounded-xl bg-n-alpha-2 border-n-slate-5 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-8/40"
          placeholder="Например: ответь короче, увереннее и предложи следующий шаг. Если оставить пустым, агент сам решит, как лучше."
        />
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-sm font-medium text-n-slate-12">Режим</div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
          <button
            v-for="option in assistantExecutionModeOptions"
            :key="option.value"
            type="button"
            class="flex flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-colors"
            :class="
              assistantExecutionMode === option.value
                ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                : 'border-n-slate-4 bg-n-alpha-2 text-n-slate-12 hover:border-n-slate-6'
            "
            @click="selectAssistantExecutionMode(option.value)"
          >
            <span class="text-sm font-medium">{{ option.label }}</span>
            <span class="text-xs leading-5 opacity-80">{{ option.hint }}</span>
          </button>
        </div>
        <p class="text-xs text-n-slate-10 mb-0">
          {{ selectedExecutionModeHint }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-sm font-medium text-n-slate-12">Модель</div>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
          <button
            v-for="option in assistantModelOptions"
            :key="option.value"
            type="button"
            class="flex flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-colors"
            :class="
              requestedModel === option.value
                ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                : 'border-n-slate-4 bg-n-alpha-2 text-n-slate-12 hover:border-n-slate-6'
            "
            @click="selectRequestedModel(option.value)"
          >
            <span class="text-sm font-medium">{{ option.label }}</span>
            <span class="text-xs leading-5 opacity-80">{{ option.hint }}</span>
          </button>
        </div>
        <p class="text-xs text-n-slate-10 mb-0">
          {{ selectedModelHint }}
        </p>
      </div>

      <div
        v-if="assistantStatus"
        class="rounded-xl border border-n-blue-5 bg-n-blue-3 px-4 py-3 text-sm text-n-blue-11"
      >
        {{ assistantStatus }}
      </div>

      <div
        v-if="assistantError"
        class="rounded-xl border border-n-ruby-5 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-11"
      >
        {{ assistantError }}
      </div>

      <div
        v-if="assistantDraft"
        class="flex min-h-0 flex-col gap-2 rounded-xl border border-n-slate-4 bg-n-alpha-2 px-4 py-4"
      >
        <div class="text-sm font-medium text-n-slate-12">Черновик ответа</div>
        <div
          class="break-words text-sm leading-6 text-n-slate-12 whitespace-pre-wrap"
        >
          {{ assistantDraft }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full shrink-0 flex-wrap items-center justify-end gap-3">
        <NextButton
          label="Закрыть"
          color="slate"
          variant="faded"
          type="button"
          @click="dismissAssistantDialog"
        />
        <NextButton
          v-if="assistantDraft"
          label="Вставить в ответ"
          color="slate"
          variant="faded"
          type="button"
          :disabled="!canInsertAssistantDraft"
          @click="insertAssistantDraft"
        />
        <NextButton
          :label="assistantRunButtonLabel"
          color="blue"
          type="button"
          :disabled="!canRunAssistant"
          :is-loading="isAssistantRunning"
          @click="runAssistant"
        />
      </div>
    </template>
  </Dialog>
</template>
